// ─────────────────────────────────────────────────────────────────────────────
// LaneImages — the images a frontier turn carries, in the chat (frontier lanes,
// Phase 4; owner rulings R28, 3B-17, 3B-18).
//
// ONLY A MESSAGE THAT CARRIES `images` IS TOUCHED. The field is set on frontier
// turns alone (the reply's `images`, or a history row's), so an Automatic /
// Local chat renders exactly as before: laneImagePlan() returns null and nothing
// here runs.
//
// FETCHED WITH THE BEARER. The image route requires auth and a bare <img src>
// cannot carry a header, so each image is fetched (api/agentLane) and shown
// from a blob URL. While it loads, a dark frame holds its place; if it cannot
// be read, a muted note says so — never a broken image.
//
// GPT'S PLACEHOLDER. GPT writes a generated image into its reply as the text
// `![alt](attachment)`. Display only: each placeholder shows the turn's next
// generated image in its place (or, if there is none to show, its alt text as a
// muted note) — never the raw markdown, never a broken image. The stored reply
// text is not changed.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { fetchLaneImage } from '../../api/agentLane';

export interface LaneImageRef {
  id: string;
  mime?: string | null;
  /** 'uploaded' (the user's image) | 'generated' (made by the model). */
  kind?: string | null;
}

const PLACEHOLDER = /!\[([^\]]*)\]\(attachment\)/g;

export interface LaneImagePlan {
  /** Generated images, in order, one for each placeholder in the text. */
  inline: LaneImageRef[];
  /** Everything not shown in a placeholder's place, shown after the text. */
  rest: LaneImageRef[];
}

function refs(value: unknown): LaneImageRef[] {
  return Array.isArray(value)
    ? value.filter((r: any) => r && typeof r.id === 'string' && /^[0-9a-f]{32}$/.test(r.id))
    : [];
}

/** null for a message with no `images` field — every Automatic / Local message. */
export function laneImagePlan(message: { text?: string; images?: unknown }): LaneImagePlan | null {
  if (!message || message.images === undefined || message.images === null) return null;
  const all = refs(message.images);
  const generated = all.filter((r) => r.kind === 'generated');
  const placeholders = (String(message.text || '').match(PLACEHOLDER) || []).length;
  const inline = generated.slice(0, placeholders);
  return { inline, rest: all.filter((r) => inline.indexOf(r) < 0) };
}

/** True when the line holds GPT's placeholder. */
export function hasAttachmentPlaceholder(line: string): boolean {
  PLACEHOLDER.lastIndex = 0;
  return PLACEHOLDER.test(line);
}

/** Hands out the plan's inline images in order, one per placeholder met. */
export function laneTaker(plan: LaneImagePlan): () => LaneImageRef | null {
  let next = 0;
  return () => (next < plan.inline.length ? plan.inline[next++] : null);
}

/** A line with its placeholders replaced by the turn's images (display only). */
export function renderAttachmentLine(
  line: string,
  take: () => LaneImageRef | null,
  renderText: (text: string) => React.ReactNode,
): React.ReactNode {
  const out: React.ReactNode[] = [];
  let last = 0;
  let n = 0;
  PLACEHOLDER.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLACEHOLDER.exec(line)) !== null) {
    if (m.index > last) out.push(<React.Fragment key={`t${n}`}>{renderText(line.slice(last, m.index))}</React.Fragment>);
    const ref = take();
    out.push(ref
      ? <LaneImage key={`i${n}`} image={ref} alt={m[1]} />
      : <span key={`i${n}`} className="text-rexy-muted/70 italic" data-testid="lane-image-alt">
          {m[1] ? `[image: ${m[1]}]` : '[image]'}
        </span>);
    last = m.index + m[0].length;
    n += 1;
  }
  if (last < line.length) out.push(<React.Fragment key="tail">{renderText(line.slice(last))}</React.Fragment>);
  return out;
}

/** One stored image, fetched with the Bearer and shown from a blob URL. */
export const LaneImage: React.FC<{ image: LaneImageRef; alt?: string }> = ({ image, alt }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let made: string | null = null;
    setSrc(null);
    setFailed(false);
    fetchLaneImage(image.id).then((blob) => {
      if (!live) return;
      if (!blob) { setFailed(true); return; }
      made = URL.createObjectURL(blob);
      setSrc(made);
    });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [image.id]);

  if (failed) {
    return (
      <span className="block my-2 text-[10px] text-rexy-muted/70 italic" data-testid="lane-image-missing">
        Image could not be loaded.
      </span>
    );
  }
  if (!src) {
    return (
      <span className="block my-2 w-48 h-32 rounded-lg border border-rexy-border bg-[#101010] shadow-inner"
        data-testid="lane-image-loading" aria-label="Loading image" />
    );
  }
  return (
    <img
      src={src}
      alt={alt || (image.kind === 'uploaded' ? 'Uploaded image' : 'Generated image')}
      className="block my-2 max-w-full max-h-96 rounded-lg border border-rexy-border shadow-md"
      data-testid="lane-image"
      data-image-id={image.id}
    />
  );
};

/** The images shown after a message's text. */
export const LaneImages: React.FC<{ images: LaneImageRef[] }> = ({ images }) => {
  if (!images.length) return null;
  return (
    <div className="flex flex-col gap-1" data-testid="lane-images">
      {images.map((image) => <LaneImage key={image.id} image={image} />)}
    </div>
  );
};
